import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useToggle } from 'Shared/utils/hooks';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import { useWorkspaceTemplates } from 'Shared/utils/hooks/use-workspace-templates';

// Types
import { ListSelectOption } from 'Wpds/components/list';
import { Workspace } from 'Shared/types/Workspace.type';
import { AgendaBuilderSteps } from '../AgendaBuilder';
import { WorkspaceTemplateType } from 'Shared/constants/types';

// Actions
import { saveTemplateAction, bulkApplyTemplateAction } from 'Shared/actions/workspaces';

// Components
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { WpListSelect } from 'Wpds/components/list';
import { WpAvatar, WpAvatarEmpty } from 'Wpds/components/avatar';
import AgendaBuilderModalWrapper from '../AgendaBuilderModalWrapper';
import WpLabelled from 'Wpds/components/labelled/WpLabelled';
import { WpCheckbox } from 'Wpds/components/checkbox';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';
import WorkspaceLoading from 'Dashboard/components/WorkspacePage/partials/WorkspaceLoading';

// Styles
import './AgendaApplyStep.scss';

export type WorkspaceOption = Workspace & ListSelectOption;

type Props = {
  workspace: Workspace;
  handleSetState: Function;
};

const AgendaApplyStep = ({ workspace, handleSetState }: Props) => {
  const dispatch = useDispatch();
  const { id: currentUserId, user } = useCurrentUser();
  const { isMedium } = useResponsiveScreenWidth({ isAtLeast: true });
  const { templates } = useWorkspaceTemplates({
    workspace,
    type: WorkspaceTemplateType.oneOnOne,
    fetchOnMount: false,
  });

  const userTemplates = templates.filter(template => template.isCustom && template.userId === currentUserId);

  const [templateName, setTemplateName] = useState('');
  const [templateIsPublic, setTemplateIsPublic] = useState(false);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<WorkspaceOption[]>([]);
  const [hasNameError, toggleHasNameError] = useToggle(false);
  const [isSaving, toggleIsSaving] = useToggle(false);

  const workspaces = useSelector(state => state.workspaces);
  const OneOnOneWorkspaces = workspaces.filter(({ isOneOnOne }) => isOneOnOne).filter(ws => ws.id !== workspace.id);

  const handleContinueClick = () => {
    if (!templateName.trim()) {
      toggleHasNameError();
      return;
    }

    toggleIsSaving();
    dispatch(
      saveTemplateAction({
        workspaceId: workspace.id,
        name: templateName,
        isPublic: templateIsPublic,
        description: '',
        excludedItemUuids: [],
        templateType: workspace.isGroup ? 'team' : 'one_on_one',
        sourcePage: 'template_wizard',
      })
    ).then(response => {
      if (selectedWorkspaces.length > 0) {
        const templateSlug = response.data.data.attributes.slug;

        dispatch(
          bulkApplyTemplateAction({
            workspacesSlugs: selectedWorkspaces.map(ws => ws.id),
            templateSlug,
            sourcePage: 'template_wizard_apply',
          })
        ).then(() => {
          window.location.reload();
        });
      } else {
        window.location.reload();
      }
    });
  };

  const handleBackClick = () => {
    handleSetState(AgendaBuilderSteps.build);
  };

  const handleSetTemplateName = value => {
    setTemplateName(value);
    hasNameError && toggleHasNameError();
  };

  const handleSelectWorkspace = (selected: WorkspaceOption[]) => {
    setSelectedWorkspaces(selected);
  };

  const handleSelectAllChange = value => {
    if (value) {
      setSelectedWorkspaces(OneOnOneWorkspaces);
    } else {
      setSelectedWorkspaces([]);
    }
  };

  useEffect(() => {
    if (user?.flowType === 'calendar_integration_2023' && userTemplates.length === 0) {
      dispatch(
        saveTemplateAction({
          workspaceId: workspace.id,
          name: 'My 1:1 Template',
          isPublic: false,
          description: '',
          excludedItemUuids: [],
          templateType: workspace.isGroup ? 'team' : 'one_on_one',
          sourcePage: 'template_wizard',
        })
      ).then(() => {
        window.location.reload();
      });
    }
  }, []);

  const baseClassName = 'AgendaApplyStep';
  const classes = classnames(baseClassName, !isMedium ? `${baseClassName}--full-width` : '');

  const customOption = ({ option }) => {
    const otherUser = option.users.find(user => user.id !== currentUserId) || null;
    return (
      <div className={`${baseClassName}__option`}>
        {otherUser ? <WpAvatar avatar={otherUser.avatar} size="sm" /> : <WpAvatarEmpty size="sm" />}
        <span className={`${baseClassName}__option__name`}>{option.label}</span>
      </div>
    );
  };

  if (user?.flowType === 'calendar_integration_2023' && userTemplates.length === 0) return <WorkspaceLoading />;

  return (
    <AgendaBuilderModalWrapper
      workspace={workspace}
      handleSetState={handleSetState}
      handleContinueClick={handleContinueClick}
      handleBackClick={handleBackClick}
      nextBtnLabel="Save & Apply"
      showLoadingOnPrimaryCta={isSaving}
    >
      <div className={classes}>
        <div className={`${baseClassName}__body`}>
          <h5 className={`${baseClassName}__body__title`}>Save & apply to your 1:1 workspaces </h5>
          <p>Name and save your template. It will show in your company’s template gallery to reuse in the future.</p>
          <div className={`${baseClassName}__body__name`}>
            <WpTextField
              id="template-name"
              label="What do you want to name this template?"
              value={templateName}
              onChange={handleSetTemplateName}
              placeholder={user?.first_name ? `${user.first_name}'s Weekly 1:1 Agenda` : 'Weekly 1:1 agenda'}
              error={hasNameError ? 'Please enter a name for this template' : ''}
              autoFocus
            />
          </div>
          <div className={`${baseClassName}__body__privacy`}>
            <WpRadioGroup
              id="template-privacy"
              label="Where do you want to save this template?"
              defaultValue={templateIsPublic ? 'public' : 'private'}
              onChange={value => setTemplateIsPublic(value === 'public')}
            >
              <WpRadioInput
                id="private"
                label="Private Gallery"
                value="private"
                descriptionText="Only you will be able to see and reuse this template"
              />
              <WpRadioInput
                id="public"
                label="Public Gallery"
                value="public"
                descriptionText="Everyone at your company will be able to see and reuse this template"
              />
            </WpRadioGroup>
          </div>
          <div className={`${baseClassName}__body__users`}>
            <WpLabelled
              id="workspace-list-select"
              label="Which 1:1 workspaces do you want to apply this template to?"
              showRequiredIndicator={false}
            >
              <WpCheckbox id="select-all-cb" label="Select all" onChange={handleSelectAllChange} />
              <div className={`${baseClassName}__body__users__list`}>
                <WpListSelect
                  id="options_current_ws"
                  options={[{ ...workspace, disabled: true }] as WorkspaceOption[]}
                  selectedOptions={[workspace] as WorkspaceOption[]}
                  qaTest="template-workspace-selected"
                  components={{
                    optionInner: customOption,
                  }}
                />
                <WpListSelect
                  id="other-one-on-one-ws"
                  options={OneOnOneWorkspaces}
                  selectedOptions={selectedWorkspaces as WorkspaceOption[]}
                  onChange={handleSelectWorkspace}
                  qaTest="template-workspace-select"
                  components={{
                    optionInner: customOption,
                  }}
                />
              </div>
            </WpLabelled>
          </div>
        </div>
      </div>
    </AgendaBuilderModalWrapper>
  );
};

export default AgendaApplyStep;
